import { useQuery } from '@tanstack/react-query'
import { ticketXdrTimelineQuery, type TicketXdrTimelineItem } from '../api/securitee'
import { severityLabel } from '../securitee/columns'
import { Spinner } from '../ui/Spinner'

/**
 * "Ce s-a intamplat" in jurul unei detectii (docs XDR 03, decizia 8): pe
 * aparatul incidentului, 30 de minute inainte si 10 dupa - evenimentele
 * Windows, detectiile si actiunile de raspuns, in ordinea lor, in UTC.
 * Detectia deschisa e marcata.
 */
export function XdrTimeline({ ticket, detection }: { ticket: number; detection: number }) {
  const query = useQuery(ticketXdrTimelineQuery(ticket, detection))

  if (query.isPending) return <Spinner />

  if (query.isError) {
    return <p className="color-red">{query.error instanceof Error ? query.error.message : 'The timeline could not be read.'}</p>
  }

  const { from, to, items, truncated } = query.data

  return (
    <section className="xdr-timeline" aria-label="What happened">
      <h5 className="xdr-timeline-head">
        What happened <span className="font-grey-cascade">{clock(from).slice(0, 5)} - {clock(to).slice(0, 5)} UTC</span>
      </h5>
      {items.length === 0 ? (
        <p className="font-grey-cascade">Nothing recorded on this device in that window.</p>
      ) : (
        <ol className="list-unstyled xdr-timeline-list">
          {items.map((item, i) => (
            <Row key={`${item.at}-${item.kind}-${i}`} item={item} />
          ))}
        </ol>
      )}
      {truncated && <p className="font-grey-cascade">Only the first 200 Windows events of the window are shown.</p>}
    </section>
  )
}

function Row({ item }: { item: TicketXdrTimelineItem }) {
  return (
    <li className={`xdr-timeline-item xdr-timeline-${item.kind}${item.current ? ' xdr-timeline-current' : ''}`}>
      <time dateTime={`${item.at.replace(' ', 'T')}Z`} className="xdr-timeline-time">
        {clock(item.at)}
      </time>
      <Kind item={item} />
      <span className="xdr-timeline-text">
        {item.text}
        {item.by ? <span className="font-grey-cascade"> by {item.by}</span> : null}
      </span>
    </li>
  )
}

function Kind({ item }: { item: TicketXdrTimelineItem }) {
  if (item.kind === 'detection') {
    const [label, cls] = severityLabel(item.severity ?? 'low')

    return <span className={`label ${cls}`}>{label}</span>
  }

  return <span className="label label-default">{item.kind === 'action' ? 'Response' : 'Event'}</span>
}

/** "2026-10-02 07:31:27" -> "07:31:27" */
function clock(at: string): string {
  return at.slice(11, 19)
}
